import { useEffect, useState, useCallback } from 'react'

/**
 * Auto-scales the dashboard when the viewport is smaller than the design
 * baseline. This replicates the effect of the user zooming out in the browser
 * so that all content (including footer buttons) remains reachable.
 *
 * Design baseline: 1920×1080. On a 1366×768 laptop the scale will be ~0.71,
 * which is equivalent to the user zooming out to ~70%.
 *
 * The scale is clamped between MIN_SCALE and 1 so it never enlarges content
 * and never shrinks below a readable threshold.
 */

const DESIGN_WIDTH = 1920
const DESIGN_HEIGHT = 1080
const MIN_SCALE = 0.55

const computeScale = (): number => {
  const sw = window.innerWidth / DESIGN_WIDTH
  const sh = window.innerHeight / DESIGN_HEIGHT
  const raw = Math.min(sw, sh)
  return Math.max(MIN_SCALE, Math.min(1, raw))
}

const useAutoScale = () => {
  const [scale, setScale] = useState<number>(computeScale)

  const update = useCallback(() => {
    setScale(computeScale())
  }, [])

  useEffect(() => {
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [update])

  return scale
}

export default useAutoScale
